import { type HeaderViewMode, getDefaultHeaderViewMode, normalizeBuId, normalizeHeaderViewMode, normalizeNumberValue, normalizeStringValue } from './utils'
import { PeriodType, StatisticsType } from '@/api/base/enums'
import type { PageHeaderFilterArgs, SelectedSalesMeta } from '@/api/home'
import dayjs from '@/utils/date-tools'
import type { CommissionPeriodDetail } from '@/api/commission'
import { PeriodBillConfirmStatus } from '@/types/enums'
import stores from '@/store'
import { UserViewMode } from '@/store/modules/auth/type'

const store = defineStore('globalSearch', () => {
  const { user, hasBuUserViewType, loginStatus } = storeToRefs(stores.authStore)
  const hasAppliedLoginDefaultHeaderView = ref(false)
  const defaultBuId = computed(() => {
    return Array.isArray(user.value.bu_ids) ? user.value.bu_ids[0] ?? '' : ''
  })

  function normalizeUserViewMode(value: unknown): HeaderViewMode {
    return normalizeHeaderViewMode(value, hasBuUserViewType.value)
  }

  // 字段级 patch 归一化：
  // 后续若 searchData 新增字段，优先在这里补齐归一化规则。
  // 原则是：
  // - 只处理“需要纠正类型 / 默认值 / 兼容旧缓存”的字段
  // - 保持函数纯粹，不做跨字段副作用
  // 这样 setSearchData 和初始化兜底都能复用同一份规则。
  function normalizeSearchPatch(searchPatch: Partial<PageHeaderFilterArgs>) {
    const normalizedPatch = { ...searchPatch }

    if ('year' in normalizedPatch) {
      normalizedPatch.year = normalizeNumberValue(normalizedPatch.year, dayjs().year())
    }
    if ('quarterly' in normalizedPatch) {
      normalizedPatch.quarterly = normalizeNumberValue(normalizedPatch.quarterly, 0)
    }
    if ('month' in normalizedPatch) {
      normalizedPatch.month = normalizeNumberValue(normalizedPatch.month, 0)
    }
    if ('startTime' in normalizedPatch) {
      normalizedPatch.startTime = normalizeStringValue(normalizedPatch.startTime)
    }
    if ('endTime' in normalizedPatch) {
      normalizedPatch.endTime = normalizeStringValue(normalizedPatch.endTime)
    }
    if ('buId' in normalizedPatch) {
      normalizedPatch.buId = normalizeBuId(normalizedPatch.buId, defaultBuId.value)
    }
    if ('salesId' in normalizedPatch) {
      normalizedPatch.salesId = normalizeStringValue(normalizedPatch.salesId)
    }
    if ('userViewType' in normalizedPatch) {
      normalizedPatch.userViewType = normalizeUserViewMode(normalizedPatch.userViewType)
    }

    return normalizedPatch
  }

  // 全量归一化：
  // 主要用于处理 persisted state 回灌，因为 pinia 持久化恢复不会经过 setSearchData。
  // 若以后 searchData 新增字段，且这个字段可能被缓存污染，也需要确保这里能覆盖到。
  function normalizeSearchData(currentSearchData: PageHeaderFilterArgs): PageHeaderFilterArgs {
    return {
      ...currentSearchData,
      ...normalizeSearchPatch(currentSearchData),
    }
  }

  // 只在字段真正被修正时回写，避免 watch(searchData) 形成无意义的重复赋值。
  function hasSearchDataChanged(currentSearchData: PageHeaderFilterArgs, nextSearchData: PageHeaderFilterArgs) {
    return currentSearchData.year !== nextSearchData.year
      || currentSearchData.quarterly !== nextSearchData.quarterly
      || currentSearchData.month !== nextSearchData.month
      || currentSearchData.startTime !== nextSearchData.startTime
      || currentSearchData.endTime !== nextSearchData.endTime
      || currentSearchData.buId !== nextSearchData.buId
      || currentSearchData.salesId !== nextSearchData.salesId
      || currentSearchData.userViewType !== nextSearchData.userViewType
      || currentSearchData.periodType !== nextSearchData.periodType
      || currentSearchData.statisticsType !== nextSearchData.statisticsType
  }

  // 默认值工厂：
  // searchData 是全局筛选参数的单一事实来源，header / 页面 / 视图切换都以它为准。
  // 这里的初始值只负责“冷启动默认态”。
  // userViewType 不做持久化：
  // - 复制浏览器标签页时，sessionStorage 会被整体克隆
  // - 若把 userViewType 也持久化，会把源标签页当前视图错误带到新标签页
  // 因此每次完整加载页面时，都按 token 返回的视图权限重新决定默认视图。
  function createDefaultSearchData(): PageHeaderFilterArgs {
    return {
      year: dayjs().year(),
      quarterly: 0,
      month: 0,
      periodType: PeriodType.Year,
      statisticsType: StatisticsType.Order,
      startTime: '',
      endTime: '',
      buId: defaultBuId.value,
      salesId: '',
      userViewType: getDefaultHeaderViewMode(hasBuUserViewType.value),
    }
  }

  const searchData = ref<PageHeaderFilterArgs>(createDefaultSearchData())
  const selectedSalesMeta = ref<SelectedSalesMeta>({
    salesId: '',
    userId: '',
    roleType: undefined,
  })
  function applyLoginDefaultHeaderViewMode() {
    if (loginStatus.value !== 2 || hasAppliedLoginDefaultHeaderView.value) {
      return
    }

    // globalSearch store 往往会早于 auth 用户信息完成初始化。
    // 因此冷启动时 createDefaultSearchData 可能先按“无 BU 权限”落成 Broker。
    // 当登录完成后，需要再基于 token 返回的 user_view_types 回填一次默认视图，
    // 这样“无缓存 / 同 tab 重登”场景才会正确优先展示 BU 视图。
    searchData.value.userViewType = getDefaultHeaderViewMode(hasBuUserViewType.value)
    hasAppliedLoginDefaultHeaderView.value = true
  }

  const headerViewMode = computed<HeaderViewMode>({
    get: () => normalizeUserViewMode(searchData.value.userViewType),
    set: (value) => {
      // Header 上切换视图时，不额外维护第二份状态，
      // 直接写回 searchData，保证旧首页 / 新首页 / Header 三者共用同一事实来源。
      searchData.value.userViewType = normalizeUserViewMode(value)
    },
  })
  // 是否显示 Header 视图切换按钮，只由“是否拥有 BU 查看视图”决定。
  // 这和 user_account_types 的业务身份判断是两条不同链路。
  const canSwitchHeaderView = computed(() => hasBuUserViewType.value)
  const isBuHeaderView = computed(() => headerViewMode.value === UserViewMode.Bu)

  // 佣金周期数据
  const commissionPeriodData = ref<CommissionPeriodDetail>({
    // 账期id
    periodId: '',
    // 周期时间
    period: '',
    // 账单状态
    status: PeriodBillConfirmStatus.Undefined,
  })

  function setSearchData(nextSearchData: Partial<PageHeaderFilterArgs>) {
    // 统一从 store 入口做 merge + normalize，避免各页面绕过归一化规则直接写值。
    searchData.value = {
      ...searchData.value,
      ...normalizeSearchPatch(nextSearchData),
    }
  }

  function setSelectedSalesMeta(nextMeta: SelectedSalesMeta) {
    selectedSalesMeta.value = {
      salesId: normalizeStringValue(nextMeta.salesId),
      userId: normalizeStringValue(nextMeta.userId),
      roleType: nextMeta.roleType,
    }
  }

  // auth store 可能晚于 global-search 初始化完成。
  // 因此需要在用户信息准备好之后，再把默认 buId 收敛到用户的第一个 bu_ids。
  watch(defaultBuId, (buId) => {
    if (!searchData.value.buId) {
      searchData.value.buId = buId
    }
  }, { immediate: true })

  // 登录成功后只回填一次默认视图：
  // - 解决 auth 晚于 globalSearch 初始化导致的默认视图错误
  // - 不干扰用户登录成功后的手动切换
  // - 登出 / 登录失效后重置标记，下一次登录重新按权限判定
  watch(loginStatus, (status) => {
    if (status === 2) {
      applyLoginDefaultHeaderViewMode()
      return
    }

    if (status === 0 || status === 3) {
      hasAppliedLoginDefaultHeaderView.value = false
    }
  }, { immediate: true })

  // 用户权限变化时，只做“合法性兜底”：
  // - 若当前账号不具备 BU_VIEW，则自动降级为 Broker
  // - 登录成功后的“默认视图回填”由上面的 loginStatus watch 单独处理
  // - 运行期内用户手动切换的值仍保留在当前 tab 内存中
  watch(hasBuUserViewType, (hasBuView) => {
    const normalizedMode = normalizeHeaderViewMode(searchData.value.userViewType, hasBuView)
    if (searchData.value.userViewType !== normalizedMode) {
      searchData.value.userViewType = normalizedMode
    }
  }, { immediate: true })

  // headerViewMode 实际代理的是 searchData.userViewType。
  // 这个 watch 的作用是兜底修正外部直接写入的非法值。
  watch(() => searchData.value.userViewType, (value) => {
    const normalizedMode = normalizeUserViewMode(value)
    if (value !== normalizedMode) {
      searchData.value.userViewType = normalizedMode
    }
  }, { immediate: true })

  // persisted state 回灌后的最终兜底清洗。
  // 如果后面新增筛选字段且需要做类型兼容，除了 normalizeSearchPatch 外，
  // 也要确认这里的比较逻辑是否需要同步补充。
  watch(searchData, (value) => {
    const normalizedSearchData = normalizeSearchData(value)
    if (hasSearchDataChanged(value, normalizedSearchData)) {
      searchData.value = normalizedSearchData
    }
  }, { deep: true, immediate: true })

  // 设置佣金周期数据
  function setCommissionPeriodData(_commissionPeriodData: CommissionPeriodDetail) {
    commissionPeriodData.value = {
      ...commissionPeriodData.value,
      ..._commissionPeriodData,
    }
  }

  return {
    searchData,
    selectedSalesMeta,
    commissionPeriodData,
    headerViewMode,
    canSwitchHeaderView,
    isBuHeaderView,
    setSearchData,
    setSelectedSalesMeta,
    setCommissionPeriodData,
  }
}, {
  // https://prazdevs.github.io/pinia-plugin-persistedstate/zh/guide/config.html
  persist: {
    key: 'globalSearch',
    storage: sessionStorage,
    // userViewType 不持久化，避免复制浏览器标签页时继承源标签页的当前视图。
    // 其他筛选项继续按 session 维度共享，保持页面刷新和站内跳转体验稳定。
    paths: [
      'searchData.year',
      'searchData.quarterly',
      'searchData.month',
      'searchData.periodType',
      'searchData.statisticsType',
      'searchData.startTime',
      'searchData.endTime',
      'searchData.buId',
      'searchData.salesId',
      'commissionPeriodData',
    ],
  },
})

export default store
